// Fetch wrapper for the REST API: JSON in, zod-validated JSON out, ApiRequestError on failure.
// The session is an httpOnly cookie; 401 anywhere means "signed out" and is handled by the caller
// (the auth layout redirects to /auth).
import type { z } from 'zod';
import { ApiErrorSchema, type ApiErrorCode } from '@solungo/core/schema';
import { API_BASE } from '@/lib/env';

export class ApiRequestError extends Error {
  readonly code: ApiErrorCode | 'network' | 'unknown';
  readonly status: number;
  constructor(code: ApiRequestError['code'], status: number, message: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
  }
  get isNetwork(): boolean {
    return this.code === 'network';
  }
}

// A 2xx body the schema rejects: the app and the API disagree on the contract. Unlike the other
// ApiRequestErrors this one is a fault of ours and goes to the tracker (lib/errors).
export class ApiResponseError extends ApiRequestError {
  readonly path: string;
  readonly issues: { path: string; code: string; message: string }[];
  constructor(path: string, status: number, error: z.ZodError) {
    super('unknown', status, 'invalid response');
    this.name = 'ApiResponseError';
    this.path = path;
    // Issue paths and codes, never the values: they may be dictionary contents
    this.issues = error.issues
      .slice(0, 10)
      .map((i) => ({ path: i.path.map(String).join('.'), code: i.code, message: i.message }));
  }
}

export function isApiError(e: unknown, code?: ApiRequestError['code']): e is ApiRequestError {
  return e instanceof ApiRequestError && (code === undefined || e.code === code);
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
export function onUnauthorized(l: Listener): () => void {
  unauthorizedListeners.add(l);
  return () => unauthorizedListeners.delete(l);
}
// For callers that probe with notifyUnauthorized: false and decide themselves (the background
// session check in loadUser)
export function notifyUnauthorized(): void {
  for (const l of unauthorizedListeners) l();
}

interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  schema: z.ZodType<T>;
  signal?: AbortSignal;
  // set false for the /me probe so a 401 does not bounce the app to /auth
  notifyUnauthorized?: boolean;
  // GET only: how long to wait before giving up as a network error; false for callers that set
  // their own limit through `signal` (the sync pull)
  timeout?: number | false;
}

// A connection that never answers (no signal while navigator.onLine stays true) would otherwise
// hang until the OS drops it, holding whatever awaits the request.
export const GET_TIMEOUT_MS = 10_000;

export async function request<T>(path: string, o: RequestOptions<T>): Promise<T> {
  const method = o.method ?? 'GET';
  const timeout = method === 'GET' ? (o.timeout ?? GET_TIMEOUT_MS) : false;
  // The timeout rejects with a TimeoutError, reported below as a network error; the caller's own
  // abort stays an AbortError
  const signal =
    timeout === false
      ? o.signal
      : o.signal
        ? AbortSignal.any([o.signal, AbortSignal.timeout(timeout)])
        : AbortSignal.timeout(timeout);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers: o.body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: o.body !== undefined ? JSON.stringify(o.body) : undefined,
      signal,
    });
  } catch (e) {
    // By name: an abort reason is not always an instance of this realm's DOMException (jsdom)
    if ((e as { name?: unknown } | null)?.name === 'AbortError') throw e;
    throw new ApiRequestError('network', 0, 'network error');
  }
  if (!res.ok) {
    let code: ApiRequestError['code'] = 'unknown';
    let message = res.statusText;
    try {
      const parsed = ApiErrorSchema.safeParse(await res.json());
      if (parsed.success) {
        code = parsed.data.error.code;
        message = parsed.data.error.message;
      }
    } catch {
      // non-JSON error body
    }
    if (res.status === 401) {
      code = 'unauthorized';
      if (o.notifyUnauthorized !== false) notifyUnauthorized();
    }
    if (res.status === 429 && code === 'unknown') code = 'rate_limited';
    throw new ApiRequestError(code, res.status, message);
  }
  const json: unknown = res.status === 204 ? {} : await res.json();
  const parsed = o.schema.safeParse(json);
  if (!parsed.success) {
    if (import.meta.env.DEV) console.error('API response failed validation', path, parsed.error);
    throw new ApiResponseError(path, res.status, parsed.error);
  }
  return parsed.data;
}

// Retry policy for TanStack Query: network errors and 5xx retry, 4xx never. Without a network there
// is nothing to retry against: fail at once so the callers fall back to the local copy.
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 3) return false;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return false;
  if (error instanceof ApiRequestError) return error.isNetwork || error.status >= 500;
  return false;
}
